"use client";

import { useState } from "react";
import { HelpCircle } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import Button from "./Button";
import BottomSheet from "./mobile/BottomSheet";
import { DS_POPOVER } from "./styles";
import { useIsDesktop } from "./useIsDesktop";

export type HelpTipContent = {
  /** Nome do indicador/bloco, como aparece na tela. */
  title: string;
  /** 1–2 frases: o que é e como ler. */
  body: string;
  /** De onde o número sai (módulo/tela). */
  source?: string;
  /** Passo a passo numerado depois do texto (ex.: o que acontece ao fechar
   *  um negócio). */
  steps?: readonly string[];
};

/**
 * Dica contextual "?" (README §3: "dica contextual vira tooltip '?' no
 * cabeçalho do bloco — nada de banner fixo de dica"). No computador abre um
 * popover ancorado; no celular, uma folha de baixo com "Entendi" (popover em
 * tela de toque fica pequeno e sem saída visível). Alvo de 44px no celular.
 */
export default function HelpTip({
  help,
  className,
}: {
  help: HelpTipContent;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const desktop = useIsDesktop();
  const label = `O que é ${help.title}?`;

  const body = (
    <div className="flex flex-col gap-2">
      <p className="text-sm leading-snug text-ink">{help.body}</p>
      {help.steps && help.steps.length > 0 && (
        <ol className="flex list-decimal flex-col gap-1 pl-4 text-ui leading-snug text-ink">
          {help.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}
      {help.source && (
        <p className="text-xs leading-snug text-ink-2">
          <span className="font-semibold">De onde vem:</span> {help.source}
        </p>
      )}
    </div>
  );

  const trigger = (
    <button
      type="button"
      aria-label={label}
      title={desktop ? label : undefined}
      onClick={desktop ? undefined : () => setOpen(true)}
      className={cn(
        "flex size-11 shrink-0 items-center justify-center rounded-md text-ink-2 transition-colors outline-none hover:bg-panel hover:text-ink focus-visible:ring-2 focus-visible:ring-ring lg:size-7",
        open && "text-ink",
        className,
      )}
    >
      <HelpCircle aria-hidden className="size-4" />
    </button>
  );

  if (desktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          align="end"
          className={cn(DS_POPOVER, "flex w-72 flex-col gap-2 p-3.5")}
        >
          <p className="text-ui font-bold text-ink">{help.title}</p>
          {body}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      {trigger}
      <BottomSheet
        open={open}
        onOpenChange={setOpen}
        title={help.title}
        description="O que é"
        footer={
          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={() => setOpen(false)}
          >
            Entendi
          </Button>
        }
      >
        {body}
      </BottomSheet>
    </>
  );
}
